const STORAGE_ACCOUNT = "loginRememberAccount";
const STORAGE_PASSWORD = "loginRememberPassword";
const STORAGE_ENABLED = "loginRememberEnabled";

const KEY_SALT = "tovid-login-credential-v1";

interface RememberedCredentials {
    account: string;
    password: string;
    enabled: boolean;
}

async function deriveKey(): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        encoder.encode(KEY_SALT),
        "PBKDF2",
        false,
        ["deriveKey"],
    );
    return crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: encoder.encode("tovid-salt"),
            iterations: 100000,
            hash: "SHA-256",
        },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"],
    );
}

async function encryptPassword(password: string): Promise<string> {
    const key = await deriveKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        new TextEncoder().encode(password),
    );
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
}

async function decryptPassword(ciphertext: string): Promise<string> {
    const key = await deriveKey();
    const combined = Uint8Array.from(atob(ciphertext), (c) => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const data = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data);
    return new TextDecoder().decode(decrypted);
}

export async function loadRememberedCredentials(): Promise<RememberedCredentials | null> {
    try {
        const enabled = localStorage.getItem(STORAGE_ENABLED) === "true";
        const account = localStorage.getItem(STORAGE_ACCOUNT) ?? "";
        if (!enabled || !account) {
            return null;
        }
        const encryptedPassword = localStorage.getItem(STORAGE_PASSWORD);
        const password = encryptedPassword ? await decryptPassword(encryptedPassword) : "";
        return { account, password, enabled: true };
    } catch {
        clearRememberedCredentials();
        return null;
    }
}

export async function saveRememberedCredentials(account: string, password: string): Promise<void> {
    const encrypted = await encryptPassword(password);
    localStorage.setItem(STORAGE_ACCOUNT, account);
    localStorage.setItem(STORAGE_PASSWORD, encrypted);
    localStorage.setItem(STORAGE_ENABLED, "true");
}

export function clearRememberedCredentials(): void {
    localStorage.removeItem(STORAGE_ACCOUNT);
    localStorage.removeItem(STORAGE_PASSWORD);
    localStorage.removeItem(STORAGE_ENABLED);
}
